<template>
  <b-card class="mb-4" header-bg-variant="custom" no-body>
    <template #header>
      <h4 v-html="$t(tKey + 'header')" class="text-white" />
    </template>
    <b-tabs pills justified class="my-3" active-nav-item-class="bg-transparent active-kpi" @input="setCurrentTab">
      <b-tab v-for="value in data" :key="value.key">
        <template #title>
          <b-card no-body class="small shadow-sm text-left text-dark mb-3 mb-lg-0">
            <b-card-header class="p-2">
              <span v-html="$t(tKey + value.key + '.header')" class="font-weight-bold" />
            </b-card-header>
            <b-card-body class="p-2">
              <div v-if="value.data[0] !== -1 && value.data[1] !== -1">
                <p class="mb-1" v-html="
                  $utils.formatToFixed(
                    value.key === 'occupation'
                      ? value.data[0] * 100
                      : value.data[0]
                  ) +
                  ' ' +
                  $tc(tKey + value.key + '.unit', value.data[0])
                " />
                <p class="mb-1 font-weight-bold" :class="
                  $utils.isPositive(value.data[0], value.data[1])
                    ? 'text-success'
                    : 'text-danger'
                ">
                  <font-awesome-icon class="mr-1" :icon="
                    $utils.isPositive(value.data[0], value.data[1])
                      ? 'angle-up'
                      : 'angle-down'
                  " />
                  {{
                      $utils.formatToFixed(
                        $utils.computeDifference(value.data[0], value.data[1])
                      )
                  }}
                  %
                </p>
              </div>
              <div class="text-muted" v-else>
                <p class="my-2" v-html="$t(tKey + 'unavailable')" />
              </div>
            </b-card-body>
          </b-card>
        </template>
        <b-card-body>
          <b-row v-if="currentTab < 2">
            <b-col>
              <b-form-checkbox id="check_cumul" v-model="cumul" name="check_cumul" :value="true"
                :unchecked-value="false" @change="setCurrentTab(currentTab)">
                {{ $t(tKey + 'cumul_over_period') }}
              </b-form-checkbox>
            </b-col>
          </b-row>
          <b-row class="mt-2" v-if="chart">
            <b-col md="6">
              <apexchart v-if="chart" type="bar" height="350" :options="chartOptions" :series="series" />
            </b-col>
            <b-col md="6">
              <apexchart v-if="chartEvo" type="bar" height="350" :options="chartOptionsEvo" :series="seriesEvo" />
            </b-col>
          </b-row>
          <div v-else v-html="$t(tKey + 'noChart')" />
        </b-card-body>
      </b-tab>
    </b-tabs>
  </b-card>
</template>

<script>
import moment from "moment";
export default {
  name: "KPICard",
  props: {
    data: { type: Array, required: true },
    chart: { type: Array, required: false },
    chartEvo: { type: Array, required: false },
  },
  data() {
    return {
      tKey: "dashboard.kpi.",
      currentTab: 0,
      cumul: false,
    };
  },
  computed: {
    chartOptions() {
      return {
        chart: {
          height: 400,
          type: "bar",
          zoom: {
            enabled: false,
          },
          toolbar: {
            show: false,
          },
        },
        dataLabels: {
          enabled: true,
          offsetY: -15,
          style: {
            colors: ["#000"],
            fontSize: ".7rem",
          },
          formatter: (value) => {
            return this.currentTab == 3
              ? value.toLocaleString("de-CH", {
                style: "percent",
                maximumFractionDigits: 2,
              })
              : value.toLocaleString("de-CH");
          },
        },
        title: {
          text: this.$t(
            this.tKey + this.data[this.currentTab].key + ".chartTitle"
          ),
          align: "left",
        },
        grid: {
          row: {
            colors: ["#f3f3f3", "transparent"],
            opacity: 0.5,
          },
        },
        xaxis: {
          categories: this.chart[0].map((c) =>
            moment()
              .locale(this.$i18n.locale)
              .set("year", c.year)
              .set("month", c.month - 1)
              .set("day", 1)
              .format("MMM YYYY")
          ),
          labels: {
            rotate: -45,
            rotateAlways: true,
            hideOverlappingLabels: false,
          },
        },
        yaxis: {
          title: {
            text: this.$t(
              this.tKey + this.data[this.currentTab].key + ".header"
            ),
          },
          labels: {
            formatter: (value) => {
              return this.currentTab == 3
                ? value.toLocaleString("de-CH", {
                  style: "percent",
                  maximumFractionDigits: 2,
                })
                : value.toLocaleString("de-CH");
            },
          },
        },
        colors: ["#0078bf", "#66baeb"],
        plotOptions: {
          bar: {
            dataLabels: {
              position: "top", // top, center, bottom
            },
          },
        },
      };
    },
    chartOptionsEvo() {
      return {
        chart: {
          height: 400,
          type: "bar",
          zoom: {
            enabled: false,
          },
          toolbar: {
            show: false,
          },
        },
        dataLabels: {
          enabled: true,
          offsetY: -15,
          style: {
            colors: ["#000"],
            fontSize: ".7rem",
          },
          formatter: (value) => {
            return value.toLocaleString("de-CH", {
              style: "percent",
              maximumFractionDigits: 2,
            });
          },
        },
        title: {
          text: this.$t(
            this.tKey + this.data[this.currentTab].key + ".chartEvoTitle"
          ),
          align: "left",
        },
        grid: {
          row: {
            colors: ["#f3f3f3", "transparent"],
            opacity: 0.5,
          },
        },
        xaxis: {
          categories: this.chartEvo.map((c) =>
            moment()
              .locale(this.$i18n.locale)
              .set("year", c.year)
              .set("month", c.month - 1)
              .set("day", 1)
              .format("MMM YYYY")
          ),
          labels: {
            rotate: -45,
            rotateAlways: true,
            hideOverlappingLabels: false,
          },
        },
        yaxis: {
          title: {
            text: this.$t(this.tKey + "evolutionPercent"),
          },
          labels: {
            formatter: (value) => {
              return value.toLocaleString("de-CH", {
                style: "percent",
                maximumFractionDigits: 2,
              });
            },
          },
        },
        colors: ["#0078bf"],
        plotOptions: {
          bar: {
            dataLabels: {
              position: "top", // top, center, bottom
            },
          },
        },
      };
    },
    series() {
      return [
        {
          name: this.$t(this.tKey + "current"),
          data: this.chart[0].map((c) => c.sum),
        },
        {
          name: this.$t(this.tKey + "previous"),
          data: this.chart[1].map((c) => c.sum),
        },
      ];
    },
    seriesEvo() {
      return [
        {
          name: this.$t(this.tKey + this.data[this.currentTab].key + ".header"),
          data: this.chartEvo.map((c) => c.evo),
        },
      ];
    },
  },
  methods: {
    setCurrentTab(index) {
      this.currentTab = index;
      this.cumul = this.currentTab < 2 ? this.cumul : false;
      this.$emit("update-kpi", this.data[index].key, this.cumul);
    },
  },
};
</script>

<style lang="scss">
.active-kpi {
  .card-header {
    background: theme-color("custom");
    color: theme-color("light");
  }
}
</style>